
import React, { useRef, useState } from 'react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';

interface FileUploaderProps {
  onFileSelect: (file: File) => void;
  acceptedFileTypes?: string;
  maxSize?: number; // in MB
  buttonText?: string;
  disabled?: boolean;
}

export const FileUploader: React.FC<FileUploaderProps> = ({
  onFileSelect,
  acceptedFileTypes = '*/*',
  maxSize = 5,
  buttonText = 'Upload File',
  disabled = false
}) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [dragActive, setDragActive] = useState(false);
  
  const handleClick = () => {
    fileInputRef.current?.click();
  };
  
  const validateFile = (file: File): boolean => {
    // Check file type if acceptedFileTypes is specified
    if (acceptedFileTypes !== '*/*') {
      const fileType = file.type;
      if (!fileType.match(acceptedFileTypes.replace('*', '.*'))) {
        toast.error(`File type not accepted. Please upload a ${acceptedFileTypes} file.`);
        return false;
      }
    }
    
    // Check file size
    const fileSizeInMB = file.size / (1024 * 1024);
    if (fileSizeInMB > maxSize) {
      toast.error(`File is too large. Maximum size is ${maxSize}MB.`);
      return false;
    }
    
    return true;
  };
  
  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      
      if (validateFile(file)) {
        onFileSelect(file);
      }
      
      // Reset the input value so the same file can be selected again
      e.target.value = '';
    }
  };
  
  const handleDrag = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  };
  
  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      const file = e.dataTransfer.files[0];
      
      if (validateFile(file)) {
        onFileSelect(file);
      }
    }
  };
  
  return (
    <div 
      className={`w-full flex flex-col items-center justify-center ${dragActive ? 'bg-gray-100' : ''}`}
      onDragEnter={handleDrag}
      onDragLeave={handleDrag}
      onDragOver={handleDrag}
      onDrop={handleDrop}
    >
      <input 
        ref={fileInputRef}
        type="file" 
        accept={acceptedFileTypes}
        className="hidden"
        onChange={handleFileChange}
        disabled={disabled}
      />
      <Button 
        type="button" 
        onClick={handleClick} 
        disabled={disabled}
        variant="outline"
        size="sm"
      >
        {buttonText}
      </Button>
      <p className="text-xs text-gray-500 mt-1">
        Drag & drop or click to select a file (max {maxSize}MB)
      </p>
    </div>
  );
};
